import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BriefcaseBusiness,
  Check,
  Globe2,
  GraduationCap,
  MessagesSquare,
  MonitorSmartphone,
  Sparkles,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/lib/api';
import { buildImageUrl } from '@/lib/utils';
import { useLang } from '@/contexts/LangContext';

function pickText(lang, arText, enText) {
  return lang === 'ar' ? arText : enText;
}

function getAboutFallbackContent(lang) {
  return {
    hero: {
      eyebrow: pickText(lang, 'من نحن', 'About Us'),
      title: pickText(lang, 'عن بيفلونت', 'About Be Fluent'),
      subtitle: pickText(
        lang,
        'مركز حديث لتدريب اللغات يساعد الطلاب والمهنيين على التواصل بثقة في بيئة دولية.',
        'A modern language training center helping students and professionals communicate confidently in an international environment.'
      ),
      intro: pickText(
        lang,
        'نحن مركز تدريب لغات ديناميكي يقدم برامج عالية الجودة للأفراد والمؤسسات. يقدّم مدربونا الخبراء برامج تفاعلية في الإنجليزية والعربية والإسبانية والألمانية والفرنسية والروسية والصينية والإيطالية.',
        'We are a dynamic language training center providing high-quality courses for individuals and organizations. Our expert instructors deliver engaging programs in English, Arabic, Spanish, German, French, Russian, Chinese, and Italian.'
      ),
      primaryLabel: pickText(lang, 'استكشف الدورات', 'Explore Courses'),
      secondaryLabel: pickText(lang, 'اختبار تحديد المستوى', 'Take Placement Test'),
    },
    mission: {
      eyebrow: pickText(lang, 'مهمتنا', 'Our Mission'),
      title: pickText(lang, 'تعلّم عملي للتواصل في الواقع', 'Practical learning for real-world communication'),
      description: pickText(
        lang,
        'مهمتنا هي مساعدة المتعلمين على تطوير مهارات تواصل حقيقية تمكّنهم من النجاح أكاديميًا ومهنيًا واجتماعيًا في بيئة عالمية. نركز على الاستخدام العملي للغة والتعلّم التفاعلي والتواصل الواعي ثقافيًا.',
        'Our mission is to help learners develop real communication skills that allow them to succeed academically, professionally, and socially in a global environment. We focus on practical language use, interactive learning, and culturally aware communication.'
      ),
    },
    offers: {
      eyebrow: pickText(lang, 'ماذا نقدم', 'What We Offer'),
      title: pickText(lang, 'خيارات مرنة لأهداف تعلم مختلفة', 'Flexible formats for different learning goals'),
      description: pickText(
        lang,
        'برامجنا مصممة لمساعدة المتعلمين على بناء الثقة ومهارات التواصل العملية في مواقف الحياة الواقعية.',
        'Our programs are designed to help learners build confidence and practical communication skills in real-life situations.'
      ),
      items: [
        { label: pickText(lang, 'دورات جماعية', 'Group courses'), icon: 'Users' },
        { label: pickText(lang, 'دروس خاصة', 'Private lessons'), icon: 'GraduationCap' },
        { label: pickText(lang, 'مجموعات مصغّرة', 'Mini-group classes'), icon: 'MessagesSquare' },
        { label: pickText(lang, 'دورات عبر الإنترنت', 'Online classes'), icon: 'MonitorSmartphone' },
        { label: pickText(lang, 'تدريب مؤسسي مخصص', 'Customized corporate training'), icon: 'BriefcaseBusiness' },
      ],
    },
    languagesSection: {
      eyebrow: pickText(lang, 'اللغات التي ندرّسها', 'Languages We Teach'),
      title: pickText(lang, 'لغات عالمية تُدرَّس بأساليب حديثة', 'Global languages taught with modern methods'),
      description: pickText(
        lang,
        'تغطي دوراتنا عددًا من أكثر لغات العالم انتشارًا، ويقدمها مدربون ذوو خبرة باستخدام أساليب تدريس حديثة.',
        "Our courses cover some of the world's most widely spoken languages, taught by experienced instructors using modern teaching methods."
      ),
      items: [
        pickText(lang, 'الإنجليزية', 'English'),
        pickText(lang, 'العربية', 'Arabic'),
        pickText(lang, 'الإسبانية', 'Spanish'),
        pickText(lang, 'الألمانية', 'German'),
        pickText(lang, 'الفرنسية', 'French'),
        pickText(lang, 'الروسية', 'Russian'),
        pickText(lang, 'الصينية', 'Chinese'),
        pickText(lang, 'الإيطالية', 'Italian'),
      ],
    },
    approach: {
      eyebrow: pickText(lang, 'منهجنا التعليمي', 'Our Teaching Approach'),
      title: pickText(lang, 'تواصل عملي وتعلّم تفاعلي', 'Communication-focused, practical, and interactive'),
      description: pickText(
        lang,
        'تركّز دوراتنا على التواصل الحقيقي والتطبيق العملي. يتعلم الطلاب من خلال دروس تفاعلية ومحادثات موجهة ومواقف حياتية تساعدهم على بناء الثقة في التحدث والاستماع والقراءة والكتابة.',
        'Our courses focus on real communication and practical application. Students learn through interactive lessons, guided conversations, and real-life scenarios that help them develop confidence in speaking, listening, reading, and writing.'
      ),
      principles: [
        pickText(lang, 'مهارات تواصل عملية', 'Practical communication skills'),
        pickText(lang, 'بيئة صفية تفاعلية', 'Interactive classroom environment'),
        pickText(lang, 'تعلّم ضمن مجموعات صغيرة', 'Small group learning'),
        pickText(lang, 'مدربون ذوو خبرة', 'Experienced instructors'),
        pickText(lang, 'مجتمع تعليمي دولي', 'International learning community'),
      ],
    },
    environment: {
      eyebrow: pickText(lang, 'بيئتنا التعليمية', 'Our Learning Environment'),
      title: pickText(lang, 'بيئة تعليمية دولية', 'An International Learning Environment'),
      paragraphs: [
        pickText(
          lang,
          'تجمع بيئتنا التعليمية طلابًا من خلفيات ثقافية ومهنية متنوعة. هذا التنوع يشجع على التعاون والممارسة اللغوية والتبادل الثقافي.',
          'Our learning environment brings together students from diverse cultural and professional backgrounds. This multicultural atmosphere encourages collaboration, language practice, and cultural exchange.'
        ),
        pickText(
          lang,
          'يستفيد الطلاب من مجتمع داعم يشجع على التواصل والنمو الشخصي.',
          'Students benefit from a supportive community where communication and personal growth are encouraged.'
        ),
      ],
    },
    reasonsSection: {
      eyebrow: pickText(lang, 'لماذا تختارنا', 'Why Choose Us'),
      title: pickText(lang, 'مصمم للمتعلمين الذين يريدون تقدمًا حقيقيًا', 'Built for learners who want real progress'),
      items: [
        pickText(lang, 'مدربون مؤهلون وذوو خبرة', 'Experienced and qualified instructors'),
        pickText(lang, 'أساليب تدريس حديثة', 'Modern teaching methods'),
        pickText(lang, 'خيارات تعلم مرنة', 'Flexible learning formats'),
        pickText(lang, 'بيئة تعليمية دولية', 'International learning environment'),
        pickText(lang, 'دورات مصممة للتواصل الحقيقي', 'Courses designed for real communication'),
      ],
    },
    finalCta: {
      eyebrow: pickText(lang, 'الخطوة التالية', 'Final CTA'),
      title: pickText(lang, 'ابدأ رحلتك اللغوية', 'Start Your Language Journey'),
      description: pickText(
        lang,
        'انضم إلى مجتمع نابض بالحياة من المتعلمين وطوّر المهارات اللغوية التي تحتاجها للنجاح الأكاديمي والمهني والشخصي.',
        'Join a vibrant community of learners and develop the language skills you need for academic, professional, and personal success.'
      ),
      primaryLabel: pickText(lang, 'استكشف الدورات', 'Explore Courses'),
      secondaryLabel: pickText(lang, 'اختبار تحديد المستوى', 'Take Placement Test'),
    },
  };
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.5, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

const iconMap = {
  Users,
  GraduationCap,
  MessagesSquare,
  MonitorSmartphone,
  BriefcaseBusiness,
};

export default function AboutPage() {
  const { lang } = useLang();
  const [about, setAbout] = useState(null);

  useEffect(() => {
    api.get('/content/about').then((res) => setAbout(res.data || null)).catch(() => setAbout(null));
  }, [lang]);

  const content = about?.content || {};
  const hero = content.hero || {};
  const mission = content.mission || {};
  const offers = content.offers || {};
  const languagesSection = content.languages || {};
  const approach = content.approach || {};
  const environment = content.environment || {};
  const reasonsSection = content.reasons || {};
  const finalCta = content.finalCta || {};
  const defaults = getAboutFallbackContent(lang);

  const offerItems = (offers.items?.length ? offers.items : defaults.offers.items).map((item) => [item.label, iconMap[item.icon] || Users]);

  const languages = languagesSection.items?.length ? languagesSection.items : defaults.languagesSection.items;
  const teachingPrinciples = approach.principles?.length ? approach.principles : defaults.approach.principles;
  const reasons = reasonsSection.items?.length ? reasonsSection.items : defaults.reasonsSection.items;

  return (
    <div data-testid="about-page" className="bf-page-canvas bf-about-page bg-[#e6e6e6] text-[#231f20] dark:bg-[#231f20] dark:text-[#e6e6e6]">
      <section className="bf-editorial-page-hero relative overflow-hidden px-4 py-14 sm:px-6 lg:px-8 lg:py-20" style={{
        backgroundImage: 'radial-gradient(circle at 18% 10%, rgba(108,139,198,0.18), transparent 26%), radial-gradient(circle at 85% 8%, rgba(35,31,32,0.08), transparent 24%)',
      }}>
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(35,31,32,0),rgba(35,31,32,0.12))] dark:bg-[linear-gradient(180deg,rgba(35,31,32,0.12),rgba(35,31,32,0.36))]" />
        <div className="relative mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.02fr_0.98fr] lg:items-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0.04} className="max-w-3xl">
            <p className="bf-brand-kicker text-[#6c8bc6]">{hero.eyebrow || defaults.hero.eyebrow}</p>
            <h1 className="bf-page-display mt-4">{hero.title || about?.title || defaults.hero.title}</h1>
            <p className="bf-page-copy mt-5 max-w-2xl text-lg text-[#1c2333]/74 dark:text-[#f4f5f7]/74">
              {hero.subtitle || about?.subtitle || defaults.hero.subtitle}
            </p>
            <p className="bf-page-copy mt-5 max-w-2xl text-[#1c2333]/72 dark:text-[#f4f5f7]/72">
              {hero.intro || defaults.hero.intro}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/courses">
                <Button className="bf-premium-cta bf-ui-button rounded-full bg-[#6c8bc6] px-7 text-white hover:bg-[#6c8bc6]">{hero.primaryLabel || defaults.hero.primaryLabel} <ArrowRight className="ml-2 h-4 w-4" /></Button>
              </Link>
              <Link to="/placement-test">
                <Button variant="outline" className="bf-outline-button rounded-full border-[#231f20]/15 px-7 dark:border-white/20">{hero.secondaryLabel || defaults.hero.secondaryLabel}</Button>
              </Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.12 }} className="overflow-hidden rounded-[2rem] border border-[#231f20]/10 bg-white/80 shadow-[0_28px_70px_-42px_rgba(15,23,42,0.45)] dark:border-white/10 dark:bg-white/6 dark:shadow-none">
            {about?.image_url || hero.image_url ? (
              <img src={buildImageUrl(about?.image_url || hero.image_url)} alt={hero.title || about?.title || defaults.hero.title} className="h-[320px] w-full object-cover sm:h-[420px]" />
            ) : (
              <div className="flex h-[320px] items-center justify-center bg-[linear-gradient(135deg,rgba(108,139,198,0.18),rgba(35,31,32,0.08))] dark:bg-[linear-gradient(135deg,rgba(108,139,198,0.24),rgba(255,255,255,0.06))] sm:h-[420px]">
                <Globe2 className="h-16 w-16 text-[#6c8bc6]" />
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <section className="px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-8 xl:grid-cols-[0.42fr_0.58fr]">
            <div>
              <p className="bf-brand-kicker text-[#6c8bc6]">{mission.eyebrow || defaults.mission.eyebrow}</p>
              <h2 className="bf-subsection-display mt-3">{mission.title || defaults.mission.title}</h2>
            </div>
            <Card className="rounded-[2rem] border border-[#231f20]/10 bg-white dark:border-white/10 dark:bg-[#231f20]">
              <CardContent className="p-6 sm:p-8">
                <p className="text-base leading-8 text-[#231f20]/74 dark:text-[#e6e6e6]/74">
                  {mission.description || defaults.mission.description}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-14 sm:px-6 lg:px-8 lg:py-20 dark:bg-[#231f20]">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6c8bc6]">{offers.eyebrow || defaults.offers.eyebrow}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{offers.title || defaults.offers.title}</h2>
            <p className="mt-4 text-base leading-8 text-[#231f20]/72 dark:text-[#e6e6e6]/72">
              {offers.description || defaults.offers.description}
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-5">
            {offerItems.map(([label, Icon], index) => (
              <motion.div key={label} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-50px' }} transition={{ delay: index * 0.06, duration: 0.45 }}>
                <Card className="h-full rounded-[1.8rem] border border-[#231f20]/10 bg-[#e6e6e6] dark:border-white/10 dark:bg-white/5">
                  <CardContent className="p-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#231f20] text-white dark:bg-white/10"><Icon className="h-5 w-5" /></div>
                    <h3 className="mt-5 text-lg font-semibold">{label}</h3>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto grid max-w-7xl gap-8 xl:grid-cols-[0.42fr_0.58fr]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6c8bc6]">{languagesSection.eyebrow || defaults.languagesSection.eyebrow}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{languagesSection.title || defaults.languagesSection.title}</h2>
            <p className="mt-4 text-base leading-8 text-[#231f20]/72 dark:text-[#e6e6e6]/72">
              {languagesSection.description || defaults.languagesSection.description}
            </p>
          </div>
          <Card className="rounded-[2rem] border border-[#231f20]/10 bg-white dark:border-white/10 dark:bg-[#231f20]">
            <CardContent className="grid gap-3 p-6 sm:grid-cols-2 sm:p-8">
              {languages.map((language) => (
                <div key={language} className="flex items-center gap-3 rounded-[1.2rem] bg-[#e6e6e6] px-4 py-3 dark:bg-white/5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#231f20] text-white dark:bg-white/10"><Globe2 className="h-4 w-4" /></div>
                  <span className="text-sm font-medium">{language}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="bg-[#231f20] px-4 py-14 text-white sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-8 xl:grid-cols-[0.42fr_0.58fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#e6e6e6]">{approach.eyebrow || defaults.approach.eyebrow}</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{approach.title || defaults.approach.title}</h2>
              <p className="mt-4 text-base leading-8 text-[#e6e6e6]/74">
                {approach.description || defaults.approach.description}
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {teachingPrinciples.map((item) => (
                <div key={item} className="flex items-start gap-3 rounded-[1.4rem] border border-white/10 bg-white/6 px-5 py-5">
                  <Check className="mt-0.5 h-4 w-4 text-[#6c8bc6]" />
                  <span className="text-sm leading-7 text-[#e6e6e6]/78">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto grid max-w-7xl gap-8 xl:grid-cols-[0.42fr_0.58fr]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6c8bc6]">{environment.eyebrow || defaults.environment.eyebrow}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{environment.title || defaults.environment.title}</h2>
          </div>
          <Card className="rounded-[2rem] border border-[#231f20]/10 bg-white dark:border-white/10 dark:bg-[#231f20]">
            <CardContent className="p-6 sm:p-8">
              <p className="text-base leading-8 text-[#231f20]/74 dark:text-[#e6e6e6]/74">
                {environment.paragraphs?.[0] || defaults.environment.paragraphs[0]}
              </p>
              <p className="mt-4 text-base leading-8 text-[#231f20]/74 dark:text-[#e6e6e6]/74">
                {environment.paragraphs?.[1] || defaults.environment.paragraphs[1]}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="bg-white px-4 py-14 sm:px-6 lg:px-8 lg:py-20 dark:bg-[#231f20]">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#6c8bc6]">{reasonsSection.eyebrow || defaults.reasonsSection.eyebrow}</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{reasonsSection.title || defaults.reasonsSection.title}</h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-5">
            {reasons.map((item, index) => (
              <motion.div key={item} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-50px' }} transition={{ delay: index * 0.06, duration: 0.45 }}>
                <Card className="h-full rounded-[1.8rem] border border-[#231f20]/10 bg-[#e6e6e6] dark:border-white/10 dark:bg-white/5">
                  <CardContent className="p-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#231f20] text-white dark:bg-white/10"><Sparkles className="h-5 w-5" /></div>
                    <p className="mt-5 text-sm font-medium leading-7">{item}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-16 sm:px-6 lg:px-8 lg:pb-24">
        <div className="mx-auto max-w-7xl">
          <Card className="overflow-hidden rounded-[2.5rem] border border-[#e6e6e6] bg-[#6c8bc6] text-white shadow-[0_32px_80px_-40px_rgba(15,23,42,0.45)]">
            <CardContent className="grid gap-8 p-8 sm:p-10 xl:grid-cols-[1fr_auto] xl:items-center">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-white/80">{finalCta.eyebrow || defaults.finalCta.eyebrow}</p>
                <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{finalCta.title || defaults.finalCta.title}</h2>
                <p className="mt-4 max-w-2xl text-base leading-8 text-white/85">
                  {finalCta.description || defaults.finalCta.description}
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row xl:flex-col">
                <Link to="/courses">
                  <Button className="h-12 rounded-full bg-white px-7 text-sm font-semibold text-[#231f20] hover:bg-white/90">{finalCta.primaryLabel || defaults.finalCta.primaryLabel} <ArrowRight className="ml-2 h-4 w-4" /></Button>
                </Link>
                <Link to="/placement-test">
                  <Button variant="outline" className="h-12 rounded-full border-white/40 bg-transparent px-7 text-sm font-semibold text-white hover:bg-white/10">{finalCta.secondaryLabel || defaults.finalCta.secondaryLabel}</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
